import React, { useEffect, useMemo, useState } from 'react';
import { Classes, Dialog } from '@blueprintjs/core';
import DocxPreviewPane from 'src/components/common/docxPreview/DocxPreviewPane';
import { displayToken, humanizeToken, IPreviewToken, PreviewTokenKind } from 'src/components/common/docxPreview/docxPreviewDom';
import { previewApiFetch, previewErrorMessage } from 'src/components/common/docxPreview/previewApi';
import { checkTemplateTokens, mergeTokenMatcher } from 'src/components/common/docxPreview/templateTokenCheck';
import DocumentTemplate from 'src/models/DocumentTemplate';

interface ITemplatePreviewDialogProps {
    /** The template to show; null keeps the dialog closed. */
    template: DocumentTemplate;
    clientNumber: number;
    projectNumber: number;
    onClose: () => void;
}

/**
 * The template library's preview: the Word template as uploaded, with every {Token} marked, split into the
 * ones generation fills in and the ones it will print exactly as typed.
 */
const TemplatePreviewDialog = (props: ITemplatePreviewDialogProps) => {
    const [data, setData] = useState<ArrayBuffer>(null);
    const [knownNames, setKnownNames] = useState<string[]>(null);
    const [tokens, setTokens] = useState<IPreviewToken[]>([]);
    const [error, setError] = useState<string>(null);
    const [highlight, setHighlight] = useState(true);
    const templateId = props.template ? props.template.id : null;
    const basePath = `v1.0/clients/${props.clientNumber}/projects/${props.projectNumber}/documentTemplate`;

    useEffect(() => {
        if (knownNames || !templateId) {
            return;
        }
        previewApiFetch(`${basePath}/merge-tokens`)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((names: string[]) => setKnownNames(names || []))
            .catch(() => setError(`LandHawk's token list could not be loaded, so tokens can't be checked.`));
    }, [templateId, knownNames, basePath]);

    useEffect(() => {
        setData(null);
        setTokens([]);
        setError(null);
        if (!templateId) {
            return undefined;
        }

        let cancelled = false;
        previewApiFetch(`${basePath}/${templateId}/file`)
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(await previewErrorMessage(response, 'The template file could not be loaded.'));
                }
                const bytes = await response.arrayBuffer();
                if (!cancelled) {
                    setData(bytes);
                }
            })
            .catch((e: Error) => {
                if (!cancelled) {
                    setError(e.message || 'The template file could not be loaded.');
                }
            });

        return () => {
            cancelled = true;
        };
    }, [templateId, basePath]);

    const isKnownToken = useMemo(() => (knownNames ? mergeTokenMatcher(knownNames) : null), [knownNames]);
    const check = useMemo(() => checkTemplateTokens(tokens, knownNames || []), [tokens, knownNames]);

    const describeToken = (token: string, kind: PreviewTokenKind) =>
        kind === 'known'
            ? `${displayToken(token)} is filled in with the ${humanizeToken(token)} when documents are generated.`
            : `${displayToken(token)} is not a LandHawk token, so it prints exactly as typed.`;

    const template = props.template;
    const isLegacy = template && template.version === 1;

    return (
        <Dialog
            isOpen={!!template}
            onClose={props.onClose}
            title={template ? `Template preview: ${template.name}` : ''}
            style={{ width: '92vw', maxWidth: '1400px', paddingBottom: 0 }}
        >
            <div className={Classes.DIALOG_BODY} style={{ display: 'flex', gap: '1rem', height: '76vh', margin: '1rem' }}>
                {error && !data ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c1d18' }}>{error}</div>
                ) : (
                    <DocxPreviewPane
                        style={{ flex: 1, border: '1px solid #d3d9e0' }}
                        data={isKnownToken ? data : null}
                        mode="template"
                        isKnownToken={isKnownToken}
                        highlight={highlight}
                        describeToken={describeToken}
                        onRendered={setTokens}
                        onRenderError={(message) => setError(`The template could not be displayed: ${message}`)}
                    />
                )}
                <aside style={{ width: '300px', overflowY: 'auto', fontSize: '13px', lineHeight: 1.45 }}>
                    {template && (
                        <p style={{ marginTop: 0, color: '#52606d' }}>
                            {[template.contractType, template.docType, template.version ? `version ${template.version}` : null]
                                .filter(Boolean)
                                .join(' · ')}
                            <br />
                            {template.originalFileName}
                        </p>
                    )}
                    {!data && !error && <p>Loading the template...</p>}
                    {data && tokens.length === 0 && knownNames && (
                        <p>
                            <strong>No {'{Tokens}'} found.</strong> Nothing in this template is filled in when documents are generated.
                        </p>
                    )}
                    {tokens.length > 0 && (
                        <>
                            <p>
                                <strong>{check.total}</strong> token{check.total === 1 ? '' : 's'} in this template. <strong>{check.filled}</strong>{' '}
                                will be filled in when documents are generated.
                            </p>
                            {check.unfilled.length > 0 ? (
                                <div
                                    style={{
                                        background: '#fde8e7',
                                        color: '#8c1d18',
                                        padding: '8px 10px',
                                        borderRadius: '4px',
                                        marginBottom: '12px',
                                    }}
                                >
                                    <strong>{check.unfilled.length} won&apos;t be filled in</strong> and will print exactly as written:
                                    <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                                        {check.unfilled.map((u) => (
                                            <li key={u.token}>
                                                <code>{displayToken(u.token)}</code>
                                                {u.count > 1 ? ` (${u.count} places)` : ''}
                                                {u.suggestion && (
                                                    <>
                                                        {' '}
                                                        - did you mean <code>{displayToken(u.suggestion)}</code>?
                                                    </>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <div
                                    style={{
                                        background: '#e3f4ea',
                                        color: '#11643a',
                                        padding: '8px 10px',
                                        borderRadius: '4px',
                                        marginBottom: '12px',
                                    }}
                                >
                                    Every token is one LandHawk fills in.
                                </div>
                            )}
                            {isLegacy && (
                                <p style={{ background: '#fff4ce', color: '#5c4400', padding: '8px 10px', borderRadius: '4px' }}>
                                    This is a version 1 template. Its documents are built by custom code that may fill tokens this check doesn&apos;t
                                    know about.
                                </p>
                            )}
                        </>
                    )}
                    <label style={{ display: 'flex', gap: '6px', alignItems: 'center', margin: '8px 0 12px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={highlight} onChange={(e) => setHighlight(e.target.checked)} />
                        Highlight tokens
                    </label>
                    <p style={{ color: '#52606d' }}>
                        <span className="lh-token lh-token--known">Green</span> tokens are filled in from the lease.{' '}
                        <span className="lh-token lh-token--unknown">Red</span> ones aren&apos;t LandHawk tokens: fix the spelling in Word and upload
                        the template again. Page breaks in this preview are approximate.
                    </p>
                </aside>
            </div>
        </Dialog>
    );
};

export default TemplatePreviewDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/TemplatePreviewDialog.tsx